import axios, { AxiosResponse, isAxiosError } from "axios";
import { BarragemCardData } from "../../components";
import {
  BarragemData,
  BarragemImage,
  BarragemProdutos,
  GeometryBarragem,
} from "../../context";
import { RequestError, setFile } from "./api";

const url = `${process.env.REACT_APP_URL}/barragens`;

const addBarragem = async (
  idEmpreendimento: string,
  nome: string,
  descricao?: string
): Promise<BarragemData | RequestError> => {
  try {
    const data: AxiosResponse<BarragemData> = await axios.post(
      `${process.env.REACT_APP_URL}/barragens`,
      {
        idEmpreendimento,
        nome,
        descricao,
      }
    );
    return processBarragem(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message: error.response?.data.message ?? "Erro ao atualizar barragem",
      };
    } else {
      throw error;
    }
  }
};

const addImage = async (
  idBarragem: number,
  image: File,
  onProgess?: (percent: number) => void
): Promise<BarragemImage | RequestError> => {
  return setFile<BarragemImage>(
    `${url}/image_upload_parts/${idBarragem}`,
    `${url}/image_upload_complete`,
    image,
    onProgess
  );
};

const removeImage = async (
  idBarragem: number,
  image: BarragemImage
): Promise<BarragemImage | RequestError> => {
  try {
    const data: AxiosResponse<BarragemImage> = await axios.delete(
      `${url}/delete_image/${idBarragem}/${image.id}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message: error.response?.data.message ?? "Erro ao atualizar barragem",
      };
    } else {
      throw error;
    }
  }
};

const addGeometry = async (
  barragem: number,
  lat: number,
  lon: number
): Promise<GeometryBarragem | RequestError> => {
  try {
    const data: AxiosResponse<GeometryBarragem> = await axios.post(
      `${url}/geometries/id/${barragem}`,
      { lat: lat, lon: lon }
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Geometry Error",
        message: error.response?.data.message ?? "Erro ao adicionar geometria",
      };
    } else {
      throw error;
    }
  }
};

const removeGeometry = async (
  geomId: number
): Promise<GeometryBarragem | RequestError> => {
  try {
    const data: AxiosResponse<GeometryBarragem> = await axios.delete(
      `${url}/geometries/id/${geomId}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Geometry Error",
        message: error.response?.data.message ?? "Erro ao excluir geometria",
      };
    } else {
      throw error;
    }
  }
};

const updateGeometry = async (
  geomData: GeometryBarragem
): Promise<GeometryBarragem | RequestError> => {
  try {
    const data: AxiosResponse<GeometryBarragem> = await axios.put(
      `${url}/geometries/id/${geomData.id}`,
      geomData
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Geometry Error",
        message: error.response?.data.message ?? "Erro ao atualizar geometria",
      };
    } else {
      throw error;
    }
  }
};

const updateBarragem = async (
  id: number,
  nome: string,
  lat: number,
  lon: number
): Promise<BarragemData | RequestError> => {
  try {
    const data: AxiosResponse<BarragemData> = await axios.put(
      `${url}/id/${id}`,
      {
        id: id,
        lat: lat,
        lon: lon,
        nome: nome,
      }
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message: error.response?.data.message ?? "Erro ao atualizar barragem",
      };
    } else {
      throw error;
    }
  }
};

const updateProdutos = async (
  idBarragem: number,
  produtos: Partial<BarragemProdutos>
): Promise<BarragemData | RequestError> => {
  try {
    const data: AxiosResponse<BarragemData> = await axios.put(
      `${url}/produtos/${idBarragem}`,
      produtos
    );
    return processBarragem(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message: error.response?.data.message ?? "Erro ao atualizar barragem",
      };
    } else {
      throw error;
    }
  }
};

const getBarragens = async (): Promise<BarragemData[] | RequestError> => {
  try {
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    const barragemData: AxiosResponse<{ barragens: BarragemData[] }> =
      await axios.get(`${url}/all`);

    const dados = barragemData.data.barragens.map(processBarragem);
    return dados;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ?? "Erro ao obter dados da barragem",
      };
    } else {
      throw error;
    }
  }
};

const getBarragemById = async (
  barragem: number
): Promise<BarragemData | RequestError> => {
  try {
    const barragemData: AxiosResponse<BarragemData> = await axios.get(
      `${url}/id/${barragem}`
    );
    return processBarragem(barragemData.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ?? "Erro ao obter dados da barragem",
      };
    } else {
      throw error;
    }
  }
};

const getBarragemCard = async (
  barragem: number
): Promise<BarragemCardData | RequestError> => {
  try {
    const barragemData: AxiosResponse<BarragemCardData> = await axios.get(
      `${url}/card/${barragem}`
    );
    return barragemData.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ?? "Erro ao obter dados da barragem",
      };
    } else {
      throw error;
    }
  }
};

const getBarragemManchas = async (
  barragem: number
): Promise<{ manchas: number[]; } | RequestError> => {
  try {
    const barragemData: AxiosResponse<{ manchas: number[]; }> = await axios.get(
      `${url}/manchas/${barragem}`
    );
    return barragemData.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ?? "Erro ao obter dados da barragem",
      };
    } else {
      throw error;
    }
  }
};

const updateRecMatLogisticos = async (
  id: number,
  text: string
): Promise<BarragemData | RequestError> => {
  try {
    const data: AxiosResponse<BarragemData> = await axios.put(
      `${url}/rec_mat_logisticos/${id}`,
      { markdown: text }
    );
    return processBarragem(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ??
          "Erro ao atualizar os Recursos Materiais e Logísticos",
      };
    } else {
      throw error;
    }
  }
};

const updateDescricao = async (
  id: number,
  text: string
): Promise<BarragemData | RequestError> => {
  try {
    const data: AxiosResponse<BarragemData> = await axios.put(
      `${url}/descricao/${id}`,
      { markdown: text }
    );
    return processBarragem(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ??
          "Erro ao atualizar a Descrição da Barragem",
      };
    } else {
      throw error;
    }
  }
};

const updateUrlPopZas = async (
  id: number,
  newUrl: string
): Promise<BarragemData | RequestError> => {
  try {
    const data: AxiosResponse<BarragemData> = await axios.put(
      `${url}/url_pop_zas/${id}`,
      { url: newUrl }
    );
    return processBarragem(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ??
          "Erro ao atualizar url do BI da população na ZAS",
      };
    } else {
      throw error;
    }
  }
};

export interface DocRef {
  id: number;
  idBarragem: number;
  dado: string;
  documento: string;
  dataCadastro: Date;
  dataModificacao?: Date;
  idUpdate: string;
}
const updateBarragemDocRef = async (
  barragemId: number,
  docs: DocRef[]
): Promise<DocRef[] | RequestError> => {
  try {
    const data: AxiosResponse<DocRef[]> = await axios.put(
      `${url}/docRef/${barragemId}`,
      docs
    );

    const dados = data.data.map((val) => {
      val.dataCadastro = new Date(val.dataCadastro);
      if (val.dataModificacao) {
        val.dataModificacao = new Date(val.dataModificacao);
      }
      return val;
    });
    return dados;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ?? "Erro ao obter dados da barragem",
      };
    } else {
      throw error;
    }
  }
};

const getBarragemDocRef = async (
  barragemId: number
): Promise<DocRef[] | RequestError> => {
  try {
    const data: AxiosResponse<DocRef[]> = await axios.get(
      `${url}/docRef/${barragemId}`
    );

    const dados = data.data.map((val) => {
      val.dataCadastro = new Date(val.dataCadastro);
      if (val.dataModificacao) {
        val.dataModificacao = new Date(val.dataModificacao);
      }
      return val;
    });
    return dados;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ?? "Erro ao obter dados da barragem",
      };
    } else {
      throw error;
    }
  }
};

// Processar dados da barragem
const processBarragem = (dados: BarragemData): BarragemData => {
  dados.dataCadastro = new Date(dados.dataCadastro);
  dados.dataModificacao &&= new Date(dados.dataCadastro);

  if (dados.produtos) {
    dados.produtos.dataCadastro = new Date(dados.produtos.dataCadastro);
    dados.produtos.dataModificacao &&= new Date(dados.produtos.dataCadastro);
  }

  return { ...dados } as BarragemData;
};

// GISTM
const updateUrlRepositorioGISTM = async (
  barragemId: number,
  newUrl: string
): Promise<BarragemData | RequestError> => {
  try {
    const response = await axios.put(`${url}/url_rep_gistm/${barragemId}`, {
      url: newUrl,
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "GISTM",
        message:
          error.response?.data.message ??
          "Erro ao atualizar a URL do Repositório GISTM",
      };
    } else {
      throw error;
    }
  }
};

//url de impactos ambientais
interface impactoAmbientaisUrl {
  barragemId: number;
  urlImpactosAmbientais: string;
}

const getImpactosAmbientaisUrl = async (
  barragemId: number
): Promise<impactoAmbientaisUrl | RequestError> => {
  try {
    const response = await axios.get(
      `${url}/url_impactos_ambientais/${barragemId}`
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Impactos Ambientais URL Error",
        message:
          error.response?.data.message ??
          "Erro ao obter os dados da URL do BI de impactos ambientais",
      };
    } else {
      throw error;
    }
  }
};

const updateImpactoAmbientaisUrl = async (
  barragemId: number,
  newUrl: string
): Promise<BarragemData | RequestError> => {
  try {
    const response = await axios.patch(
      `${url}/url_impactos_ambientais/${barragemId}`,
      {
        urlImpactosAmbientais: newUrl,
      }
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Impactos Ambientais URL Error",
        message:
          error.response?.data.message ??
          "Erro ao atualizar a URL do BI de impactos ambientais",
      };
    } else {
      throw error;
    }
  }
};

const deleteImpactoAmbientaisUrl = async (
  barragemId: number
): Promise<BarragemData | RequestError> => {
  try {
    const response = await axios.delete(
      `${url}/url_impactos_ambientais/${barragemId}`
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ??
          "Erro ao remover a URL do BI de impactos ambientais",
      };
    } else {
      throw error;
    }
  }
};

const updateDiagnosticoCultural = async (
  id: number,
  text: string
): Promise<BarragemData | RequestError> => {
  try {
    const data: AxiosResponse<BarragemData> = await axios.put(
      `${url}/diagnostico_cultural/${id}`,
      { markdown: text }
    );
    return processBarragem(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Barragem Error",
        message:
          error.response?.data.message ??
          "Erro ao atualizar Diagnóstico Cultural",
      };
    } else {
      throw error;
    }
  }
};

export const ApiBarragem = {
  addImage,
  addBarragem,
  removeImage,
  addGeometry,
  getBarragens,
  updateBarragem,
  updateProdutos,
  updateGeometry,
  removeGeometry,
  getBarragemById,
  getBarragemCard,
  updateDescricao,
  updateUrlPopZas,
  getBarragemDocRef,
  getBarragemManchas,
  updateBarragemDocRef,
  updateRecMatLogisticos,
  getImpactosAmbientaisUrl,
  updateUrlRepositorioGISTM,
  updateImpactoAmbientaisUrl,
  deleteImpactoAmbientaisUrl,
  updateDiagnosticoCultural,
};
